import React from 'react';
import { Link } from './LocaleLink';

import { openConsentSettings } from '../utils/consent';
import { useLang } from '../utils/i18n';
import { localizedRouteLabel } from '../utils/chromeLabels';

const Footer: React.FC = () => {
  const { lang, t } = useLang();
  const label = (route: string, fallback: string) => localizedRouteLabel(lang, route, fallback);
  return (
  <footer id="site-footer" className="bg-gray-900 text-gray-300 mt-auto">
    {/* Ad — footer area, well below the fold on every page. */}


    {/* Compact directory footer. The link set remains complete, but the tools
        use two columns so the footer does not consume a full desktop viewport. */}
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 border-t border-gray-800">
      <div className="grid grid-cols-2 lg:grid-cols-12 gap-x-8 gap-y-7 mb-7">
        {/* Brand */}
        <div className="col-span-2 lg:col-span-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-md bg-violet-500 flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2.2">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 3v9l5.5 5.5" strokeLinecap="round" />
                <circle cx="12" cy="12" r="1.8" fill="currentColor" stroke="none" />
              </svg>
            </div>
            <span className="font-extrabold text-white text-base">TheNameWheel</span>
          </div>
          <p className="text-xs text-gray-400 leading-relaxed mb-4">
            {t('tool.home.intro')}
          </p>
          <a
            href="https://discord.gg/FKjedfPxTf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 bg-[#5865F2] hover:bg-[#4752C4] text-white rounded-md transition-colors text-sm font-medium w-fit"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
              <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/>
            </svg>
            Discord
          </a>
        </div>

        {/* Tool */}
        <div className="col-span-2 lg:col-span-4 min-w-0">
          <h3 className="text-white font-semibold text-sm mb-3">{t('chrome.tool')}</h3>
          <ul className="grid grid-cols-2 gap-x-5 gap-y-1.5 text-sm">
            <li><Link to="/" className="hover:text-white transition-colors">{label('/', 'Spin the Wheel')}</Link></li>
            <li><Link to="/image-picker-wheel/" className="hover:text-white transition-colors">{label('/image-picker-wheel/', 'Image Picker Wheel')}</Link></li>
            <li><Link to="/weighted-wheel-spinner/" className="hover:text-white transition-colors">{label('/weighted-wheel-spinner/', 'Weighted Wheel')}</Link></li>
            <li><Link to="/random-team-generator/" className="hover:text-white transition-colors">{label('/random-team-generator/', 'Random Team Generator')}</Link></li>
            <li><Link to="/number-wheel/" className="hover:text-white transition-colors">{label('/number-wheel/', 'Number Wheel')}</Link></li>
            <li><Link to="/wheel-of-fortune-maker/" className="hover:text-white transition-colors">{label('/wheel-of-fortune-maker/', 'Wheel of Fortune Maker')}</Link></li>
            <li><Link to="/gallery/" className="hover:text-white transition-colors">{label('/gallery/', 'Template Gallery')}</Link></li>
            <li><Link to="/names/" className="hover:text-white transition-colors">{label('/names/', 'Name Lists')}</Link></li>
            <li><Link to="/linked-wheels/" className="hover:text-white transition-colors">{label('/linked-wheels/', 'Linked Wheels')}</Link></li>
            <li><Link to="/classes/" className="hover:text-white transition-colors">{label('/classes/', 'Your Classes')}</Link></li>
            <li><Link to="/classroom/resources/" className="hover:text-white transition-colors">{label('/classroom/resources/', 'Teacher Resources')}</Link></li>
            <li><Link to="/blog/" className="hover:text-white transition-colors">{label('/blog/', 'Blog')}</Link></li>
            {/* Gaming & Pop Culture */}
            <li className="col-span-2 pt-2 mt-1 border-t border-gray-800">
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-fuchsia-400 mb-1">
                🎮 {t('chrome.gaming')}
              </span>
            </li>
            <li><Link to="/anime-character-picker-wheel/" className="hover:text-fuchsia-300 text-gray-400 transition-colors">{label('/anime-character-picker-wheel/', 'Anime Character Picker')}</Link></li>
            <li><Link to="/fortnite-drop-spot-generator-wheel/" className="hover:text-fuchsia-300 text-gray-400 transition-colors">{label('/fortnite-drop-spot-generator-wheel/', 'Fortnite Drop Spot')}</Link></li>
            <li><Link to="/valorant-agent-picker-wheel/" className="hover:text-fuchsia-300 text-gray-400 transition-colors">{label('/valorant-agent-picker-wheel/', 'Valorant Agent Picker')}</Link></li>
            <li><Link to="/roblox-game-selector-wheel/" className="hover:text-fuchsia-300 text-gray-400 transition-colors">{label('/roblox-game-selector-wheel/', 'Roblox Game Selector')}</Link></li>
          </ul>
        </div>


        {/* Info */}
        <div className="col-span-1 lg:col-span-3 min-w-0">
          <h3 className="text-white font-semibold text-sm mb-3">{t('chrome.info')}</h3>
          <ul className="space-y-1.5 text-sm">
            <li><Link to="/how-to/" className="hover:text-white transition-colors">{t('nav.howto')}</Link></li>
            <li><Link to="/faq/" className="hover:text-white transition-colors">{t('nav.faq')}</Link></li>
            <li><Link to="/about/" className="hover:text-white transition-colors">{t('nav.about')}</Link></li>
            <li><Link to="/contact/" className="hover:text-white transition-colors">{t('nav.contact')}</Link></li>
            <li><Link to="/embed-spinner-wheel/" className="hover:text-white transition-colors">{label('/embed-spinner-wheel/', 'Embed Spinner Wheel')}</Link></li>
            <li className="pt-2 mt-2 border-t border-gray-800 text-gray-400 font-semibold text-xs uppercase tracking-wider">{t('chrome.compare')}</li>
            <li><Link to="/compare/wheel-of-names-alternative/" className="hover:text-white transition-colors">{label('/compare/wheel-of-names-alternative/', 'Wheel of Names')}</Link></li>
            <li><Link to="/compare/random-name-picker-no-account/" className="hover:text-white transition-colors">{label('/compare/random-name-picker-no-account/', 'No Account Pickers')}</Link></li>
            <li><Link to="/compare/teampickerwheel-alternative/" className="hover:text-white transition-colors">{label('/compare/teampickerwheel-alternative/', 'TeamPickerWheel')}</Link></li>
          </ul>
        </div>

        {/* Legal */}
        <div className="col-span-1 lg:col-span-2 min-w-0">
          <h3 className="text-white font-semibold text-sm mb-3">{t('chrome.legal')}</h3>
          <ul className="space-y-1.5 text-sm">
            <li><Link to="/privacy/" className="hover:text-white transition-colors">{t('chrome.privacyPolicy')}</Link></li>
            <li><Link to="/terms/" className="hover:text-white transition-colors">{t('chrome.termsOfUse')}</Link></li>
            <li>
              {/* Withdrawal must be as easy as consent, so this sits alongside
                  the policy links rather than being buried in the page. */}
              <button
                type="button"
                onClick={openConsentSettings}
                className="hover:text-white transition-colors text-start"
              >
                {t('chrome.cookieSettings')}
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-gray-800 pt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-gray-400">
          &copy; {new Date().getFullYear()} TheNameWheel. {t('chrome.rights')}
        </p>
        <div className="flex items-center gap-4 text-xs text-gray-400">
          <Link to="/privacy/" className="hover:text-white transition-colors">{t('chrome.privacyPolicy')}</Link>
          <Link to="/terms/" className="hover:text-white transition-colors">{t('chrome.termsOfUse')}</Link>
          <Link to="/faq/" className="hover:text-white transition-colors">{t('nav.faq')}</Link>
          <Link to="/contact/" className="hover:text-white transition-colors">{t('nav.contact')}</Link>
        </div>
      </div>
    </div>
  </footer>
  );
};

export default Footer;

